import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { tasksAPI, projectsAPI } from '../lib/api';
import { 
    Plus, 
    Pencil, 
    Trash2, 
    ListTodo,
    DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

export default function Tasks() {
    const [tasks, setTasks] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [formData, setFormData] = useState({ name: '', hourly_rate: '', project_id: '' });
    const [saving, setSaving] = useState(false);
    const [filterProject, setFilterProject] = useState('all');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [tasksRes, projectsRes] = await Promise.all([
                tasksAPI.getAll(),
                projectsAPI.getAll()
            ]);
            setTasks(tasksRes.data);
            setProjects(projectsRes.data);
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const openCreateDialog = () => {
        setSelectedTask(null);
        setFormData({ name: '', hourly_rate: '', project_id: projects[0]?.id || '' });
        setDialogOpen(true);
    };

    const openEditDialog = (task) => {
        setSelectedTask(task);
        setFormData({ 
            name: task.name, 
            hourly_rate: task.hourly_rate.toString(), 
            project_id: task.project_id 
        });
        setDialogOpen(true);
    };

    const openDeleteDialog = (task) => {
        setSelectedTask(task);
        setDeleteDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        const data = {
            ...formData,
            hourly_rate: parseFloat(formData.hourly_rate)
        };

        try {
            if (selectedTask) {
                await tasksAPI.update(selectedTask.id, data);
                toast.success('Task updated');
            } else {
                await tasksAPI.create(data);
                toast.success('Task created');
            }
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save task');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            await tasksAPI.delete(selectedTask.id);
            toast.success('Task deleted');
            setDeleteDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error('Failed to delete task');
        }
    };

    const getProjectName = (projectId) => {
        return projects.find(p => p.id === projectId)?.name || 'Unknown';
    };

    const filteredTasks = filterProject === 'all' 
        ? tasks 
        : tasks.filter(t => t.project_id === filterProject);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="text-zinc-500">Loading...</div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-8" data-testid="tasks-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in">
                    <div>
                        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Tasks
                        </h1>
                        <p className="text-lg text-zinc-500">
                            Manage tasks with hourly rates
                        </p>
                    </div>
                    <Button 
                        onClick={openCreateDialog} 
                        className="swiss-btn-primary"
                        disabled={projects.length === 0}
                        data-testid="create-task-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        New Task
                    </Button>
                </div>

                {projects.length === 0 ? (
                    <div className="swiss-card animate-fade-in">
                        <div className="empty-state">
                            <ListTodo className="empty-state-icon" />
                            <h3 className="text-xl font-semibold text-zinc-900 mb-2">Create a project first</h3>
                            <p className="text-zinc-500">You need at least one project to create tasks</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Filter */}
                        <div className="flex items-center gap-4 animate-fade-in">
                            <Label className="text-zinc-500">Filter by project:</Label>
                            <Select value={filterProject} onValueChange={setFilterProject}>
                                <SelectTrigger className="w-64" data-testid="filter-project-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Projects</SelectItem>
                                    {projects.map(project => (
                                        <SelectItem key={project.id} value={project.id}>
                                            {project.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Tasks Table */}
                        {filteredTasks.length === 0 ? (
                            <div className="swiss-card animate-fade-in">
                                <div className="empty-state">
                                    <ListTodo className="empty-state-icon" />
                                    <h3 className="text-xl font-semibold text-zinc-900 mb-2">No tasks yet</h3>
                                    <p className="text-zinc-500 mb-4">Create your first task to start tracking time</p>
                                    <Button onClick={openCreateDialog} className="swiss-btn-primary" data-testid="create-first-task-btn">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Create Task
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="swiss-card animate-fade-in overflow-x-auto">
                                <table className="swiss-table">
                                    <thead>
                                        <tr>
                                            <th>Task Name</th>
                                            <th>Project</th>
                                            <th>Hourly Rate</th>
                                            <th className="w-24">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredTasks.map((task) => (
                                            <tr key={task.id} className="data-row" data-testid={`task-row-${task.id}`}>
                                                <td>
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 bg-zinc-100 rounded-sm flex items-center justify-center">
                                                            <ListTodo className="w-4 h-4 text-zinc-600" />
                                                        </div>
                                                        <span className="font-medium text-zinc-900">{task.name}</span>
                                                    </div>
                                                </td>
                                                <td className="text-zinc-600">{getProjectName(task.project_id)}</td>
                                                <td>
                                                    <div className="flex items-center gap-1 text-emerald-600 font-semibold">
                                                        <DollarSign className="w-4 h-4" />
                                                        {task.hourly_rate.toFixed(2)}/hr
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="flex gap-1">
                                                        <Button 
                                                            variant="ghost" 
                                                            size="icon" 
                                                            onClick={() => openEditDialog(task)}
                                                            className="h-8 w-8 text-zinc-500 hover:text-zinc-900"
                                                            data-testid={`edit-task-${task.id}`}
                                                        >
                                                            <Pencil className="w-4 h-4" />
                                                        </Button>
                                                        <Button 
                                                            variant="ghost" 
                                                            size="icon" 
                                                            onClick={() => openDeleteDialog(task)}
                                                            className="h-8 w-8 text-zinc-500 hover:text-red-600"
                                                            data-testid={`delete-task-${task.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {selectedTask ? 'Edit Task' : 'Create Task'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="task-form">
                        <div className="form-group">
                            <Label htmlFor="project" className="form-label">Project</Label>
                            <Select 
                                value={formData.project_id} 
                                onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                            >
                                <SelectTrigger data-testid="task-project-select">
                                    <SelectValue placeholder="Select a project" />
                                </SelectTrigger>
                                <SelectContent>
                                    {projects.map(project => (
                                        <SelectItem key={project.id} value={project.id}>
                                            {project.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="form-group">
                            <Label htmlFor="name" className="form-label">Task Name</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="e.g., Frontend Development"
                                required
                                className="swiss-input"
                                data-testid="task-name-input"
                            />
                        </div>
                        <div className="form-group">
                            <Label htmlFor="hourly_rate" className="form-label">Hourly Rate ($)</Label>
                            <Input
                                id="hourly_rate"
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.hourly_rate}
                                onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                                placeholder="e.g., 75.00"
                                required
                                className="swiss-input"
                                data-testid="task-rate-input"
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" className="swiss-btn-primary" disabled={saving} data-testid="task-submit-btn">
                                {saving ? 'Saving...' : selectedTask ? 'Update' : 'Create'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Task?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete "{selectedTask?.name}" and all associated time entries. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDelete}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="confirm-delete-task"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}
